import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button, Card, Col, Input, message, Radio, Row, Space, Table, Tag, Typography,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import {
  ServiceOrder, OrderStatus, ORDER_STATUS_LABELS, ORDER_STATUS_COLORS, ORDER_STATUSES,
  listOrders,
} from '../api/serviceOrders';

const { Title } = Typography;

const ServiceOrdersListPage: React.FC = () => {
  const { hasPermission } = useAuthStore();
  const canWrite = hasPermission('orders:write');
  const navigate = useNavigate();

  const [items, setItems] = useState<ServiceOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(30);
  const [search, setSearch] = useState('');
  const [statusF, setStatusF] = useState<OrderStatus | 'all'>('all');
  const [legalEntityF, setLegalEntityF] = useState<'all' | 'autoregium' | 'fomin'>('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listOrders({
        page, per_page: perPage,
        search: search || undefined,
        status: statusF === 'all' ? undefined : statusF,
        legal_entity: legalEntityF === 'all' ? undefined : legalEntityF,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось загрузить наряды');
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search, statusF, legalEntityF]);

  useEffect(() => { load(); }, [load]);

  const columns = useMemo(() => [
    {
      title: 'Дата', dataIndex: 'created_at', key: 'created_at', width: 100,
      render: (v: string) => (v ? new Date(v).toLocaleDateString('ru-RU') : '—'),
    },
    {
      title: '№', dataIndex: 'number', key: 'number', width: 150,
      render: (v: string | null, r: ServiceOrder) => (
        <Space size={4}>
          <span>{v || '—'}</span>
          {r.order_type === 'oil_change' && <Tag color="gold">Масло</Tag>}
          {r.order_type === 'quick_sale' && <Tag color="green">Продажа</Tag>}
          {r.has_unreceived_pending && (
            <Tag color="red" title="Есть позиции без оприходования (марки нет в 1С — сбой ЭДО)">не в обороте</Tag>
          )}
          {r.deletion_state === 'pending' && (
            <Tag color="red" title="Помечен на удаление — удалите документы в 1С вручную">на удаление</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Статус', dataIndex: 'status', key: 'status', width: 140,
      render: (v: OrderStatus) => <Tag color={ORDER_STATUS_COLORS[v]}>{ORDER_STATUS_LABELS[v]}</Tag>,
    },
    {
      title: 'Клиент', dataIndex: 'counterparty', key: 'counterparty',
      render: (v: string | null) => v || <span style={{ color: '#999' }}>—</span>,
    },
    {
      title: 'Автомобиль', dataIndex: 'vehicle', key: 'vehicle',
      render: (v: string | null, r: ServiceOrder) => v || r.license_plate || <span style={{ color: '#999' }}>—</span>,
    },
    {
      title: 'Гос. номер', dataIndex: 'license_plate', key: 'license_plate', width: 110,
      render: (v: string | null) => v || <span style={{ color: '#999' }}>—</span>,
    },
    {
      title: 'Услуги', dataIndex: 'total_services', key: 'total_services', width: 110,
      align: 'right' as const,
      render: (v: number) => Number(v) > 0 ? `${Number(v).toLocaleString('ru-RU')} ₽` : <span style={{ color: '#999' }}>—</span>,
    },
    {
      title: 'Запчасти', dataIndex: 'total_parts', key: 'total_parts', width: 110,
      align: 'right' as const,
      render: (v: number) => Number(v) > 0 ? `${Number(v).toLocaleString('ru-RU')} ₽` : <span style={{ color: '#999' }}>—</span>,
    },
    {
      title: 'Итого', dataIndex: 'total', key: 'total', width: 130,
      align: 'right' as const,
      render: (v: number) => <b>{Number(v).toLocaleString('ru-RU')} ₽</b>,
    },
    {
      title: 'Теги', dataIndex: 'tags', key: 'tags', width: 140,
      render: (tags: string[] | null) => tags?.length ? (
        <Space size={4} wrap>{tags.map((t) => <Tag key={t}>{t}</Tag>)}</Space>
      ) : null,
    },
  ], []);

  return (
    <Card>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }} gutter={[8, 8]}>
        <Col><Title level={4} style={{ margin: 0 }}>Заказ-наряды</Title></Col>
        <Col>
          <Space wrap>
            <Radio.Group value={legalEntityF} onChange={(e) => { setPage(1); setLegalEntityF(e.target.value); }}>
              <Radio.Button value="all">Все</Radio.Button>
              <Radio.Button value="autoregium">АвтоРегиум</Radio.Button>
              <Radio.Button value="fomin">Фомин</Radio.Button>
            </Radio.Group>
            <Radio.Group value={statusF} onChange={(e) => { setPage(1); setStatusF(e.target.value); }}>
              <Radio.Button value="all">Все</Radio.Button>
              {ORDER_STATUSES.map(s => (
                <Radio.Button key={s} value={s}>{ORDER_STATUS_LABELS[s]}</Radio.Button>
              ))}
            </Radio.Group>
            <Input.Search
              placeholder="Поиск"
              allowClear
              onSearch={(val) => { setPage(1); setSearch(val); }}
              style={{ width: 180 }}
            />
            {canWrite && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/service-orders/new')}>
                Новый наряд
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={items}
        loading={loading}
        rowClassName={(r) => (r.status === 'Выполнено' ? 'so-row-done' : '')}
        onRow={(r) => ({
          // Открытие в виде по order_type (OilChangePage.md §1 п.3, QuickSalePage.md §4)
          onClick: () => navigate(
            r.order_type === 'oil_change'
              ? `/oil-change/${r.id}`
              : r.order_type === 'quick_sale'
                ? `/quick-sale/${r.id}`
                : `/service-orders/${r.id}?filter=${legalEntityF}`
          ),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: page, pageSize: perPage, total, showSizeChanger: false,
          onChange: (p) => setPage(p),
        }}
      />
    </Card>
  );
};

export default ServiceOrdersListPage;
